import React, { useMemo, useState } from 'react';
import { CreditCard, Search, Download, Eye, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle, Pause, Play, MoreVertical, Gift, ArrowUpRight, Users, Star, Activity, FileText, ChevronRight, X, Settings, BarChart3, ShieldAlert, Zap, Calendar, DollarSign, ArrowRight, Check, AlertOctagon, RefreshCcw, User, Plus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { mockSubscriptions, mockTariffPlans, mockAnalytics } from './subscriptions/subscriptionMockData';
import { Subscription, SubscriptionType, SubscriptionPlan, SubscriptionStatus, PaymentError } from './subscriptions/subscriptionTypes';
interface AdminSubscriptionsFullManagerProps {
  userSubscriptions: any[];
  users: any[];
  setActiveTab: (tab: string) => void;
  success: (msg: string) => void;
  onCancelSubscription?: (id: number) => void;
}
const planLabels: Record<SubscriptionPlan, string> = {
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
  reports_bundle: 'Отчеты',
  courses_access: 'Курсы',
  vip_consultations: 'VIP Консультации'
};
const statusConfig: Record<SubscriptionStatus, {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral' | 'pro';
}> = {
  active: {
    label: 'Активна',
    variant: 'success'
  },
  trial: {
    label: 'Пробная',
    variant: 'pro'
  },
  paused: {
    label: 'Пауза',
    variant: 'warning'
  },
  cancelled: {
    label: 'Отменена',
    variant: 'neutral'
  },
  expired: {
    label: 'Истекла',
    variant: 'neutral'
  },
  payment_error: {
    label: 'Ошибка оплаты',
    variant: 'error'
  }
};
export function AdminSubscriptionsFullManager({
  success,
  onCancelSubscription
}: AdminSubscriptionsFullManagerProps) {
  const [activeView, setActiveView] = useState<'list' | 'errors' | 'plans' | 'analytics'>('list');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(mockSubscriptions);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  // Filters state
  const [filters, setFilters] = useState({
    type: 'all' as SubscriptionType | 'all',
    plan: 'all' as SubscriptionPlan | 'all',
    status: 'all' as SubscriptionStatus | 'all',
    autoRenew: 'all' as 'true' | 'false' | 'all',
    source: 'all'
  });
  // Top metric quick filter
  const [metricFilter, setMetricFilter] = useState<string | null>(null);
  // Derived metrics
  const metrics = useMemo(() => {
    return {
      total: subscriptions.length,
      active: subscriptions.filter(s => s.status === 'active').length,
      trial: subscriptions.filter(s => s.status === 'trial').length,
      cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
      errors: subscriptions.filter(s => s.status === 'payment_error').length,
      mrr: subscriptions.filter(s => s.status === 'active' || s.status === 'trial').reduce((acc, s) => acc + (s.billingPeriod === 'monthly' ? s.price : s.price / 12), 0),
      newThisMonth: 14,
      renewals: 156 // Mock
    };
  }, [subscriptions]);
  // Filtering logic
  const filteredSubs = useMemo(() => {
    return subscriptions.filter(s => {
      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!s.userName.toLowerCase().includes(q) && !s.id.toLowerCase().includes(q) && !s.userEmail.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Metric quick filters
      if (metricFilter === 'active' && s.status !== 'active') return false;
      if (metricFilter === 'trial' && s.status !== 'trial') return false;
      if (metricFilter === 'cancelled' && s.status !== 'cancelled') return false;
      if (metricFilter === 'errors' && s.status !== 'payment_error') return false;
      // Advanced filters
      if (filters.type !== 'all' && s.type !== filters.type) return false;
      if (filters.plan !== 'all' && s.plan !== filters.plan) return false;
      if (filters.status !== 'all' && s.status !== filters.status) return false;
      if (filters.autoRenew !== 'all') {
        const isAuto = s.autoRenew ? 'true' : 'false';
        if (filters.autoRenew !== isAuto) return false;
      }
      if (filters.source !== 'all' && s.source !== filters.source) return false;
      return true;
    }).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [subscriptions, searchQuery, filters, metricFilter]);
  const paginatedSubs = filteredSubs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredSubs.length / itemsPerPage);
  const selectedSub = useMemo(() => subscriptions.find(s => s.id === selectedSubId), [subscriptions, selectedSubId]);
  // Actions
  const handleAction = (id: string, action: string) => {
    setSubscriptions(prev => prev.map(s => {
      if (s.id !== id) return s;
      const newEvent = {
        id: `EVT-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: 'system' as any,
        description: `Действие: ${action}`,
        actor: 'admin' as const
      };
      let updates: Partial<Subscription> = {
        history: [newEvent, ...s.history]
      };
      switch (action) {
        case 'cancel':
          updates.status = 'cancelled';
          updates.autoRenew = false;
          if (onCancelSubscription) onCancelSubscription(parseInt(id.split('-')[1]));
          break;
        case 'pause':
          updates.status = 'paused';
          break;
        case 'resume':
          updates.status = 'active';
          break;
        case 'toggle_auto_renew':
          updates.autoRenew = !s.autoRenew;
          break;
        case 'manual_renew':
          updates.endDate = new Date(new Date(s.endDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          break;
      }
      return {
        ...s,
        ...updates
      };
    }));
    success(`Действие "${action}" выполнено для ${id}`);
  };
  const handleExportCSV = () => {
    success(`Экспорт ${filteredSubs.length} записей начат`);
  };
  // Renderers
  const renderKpi = (key: string, label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string) => <Card padding="sm" hoverable className={`rounded-xl shadow-none border transition-all cursor-pointer group ${metricFilter === key ? 'border-indigo-400 ring-1 ring-indigo-400 bg-indigo-50/30' : 'border-slate-200 bg-white'}`} onClick={() => {
    setMetricFilter(metricFilter === key ? null : key);
    setCurrentPage(1);
  }} data-id="element-7212">
      <div className="flex items-center justify-between mb-2" data-id="element-7213">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-7214">
          {icon}
        </div>
        {metricFilter === key && <CheckCircle2 className="w-4 h-4 text-indigo-500" data-id="element-7215" />}
      </div>
      <div data-id="element-7216">
        <div className={`text-xl font-bold ${textClass}`} data-id="element-7217">{value}</div>
        <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mt-0.5" data-id="element-7218">
          {label}
        </div>
      </div>
    </Card>;
  const renderRightPanel = () => {
    if (!selectedSub) return null;
    return <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] bg-white shadow-2xl border-l border-slate-200 z-50 flex flex-col transform transition-transform duration-300" data-id="element-7219">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-7220">
          <div data-id="element-7221">
            <div className="text-xs font-mono text-slate-500 mb-1" data-id="element-7222">
              {selectedSub.id}
            </div>
            <h3 className="text-lg font-bold text-slate-900" data-id="element-7223">
              {selectedSub.userName}
            </h3>
          </div>
          <button onClick={() => setSelectedSubId(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-7224">
            <X className="w-5 h-5" data-id="element-7225" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-7226">
          {/* Status & Plan Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-7227">
            <div className="flex items-center justify-between mb-4" data-id="element-7228">
              <Badge variant={statusConfig[selectedSub.status].variant} data-id="element-7229">
                {statusConfig[selectedSub.status].label}
              </Badge>
              <div className="text-sm font-bold text-slate-900" data-id="element-7230">
                {selectedSub.price} {selectedSub.currency} /{' '}
                {selectedSub.billingPeriod === 'monthly' ? 'мес' : 'год'}
              </div>
            </div>
            <div className="space-y-3" data-id="element-7231">
              <div className="flex justify-between text-sm" data-id="element-7232">
                <span className="text-slate-500" data-id="element-7233">План</span>
                <span className="font-medium text-slate-900" data-id="element-7234">
                  {planLabels[selectedSub.plan]}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-7235">
                <span className="text-slate-500" data-id="element-7236">Тип</span>
                <span className="font-medium text-slate-900 flex items-center gap-1" data-id="element-7237">
                  {selectedSub.type === 'astrologer' ? <Star className="w-3 h-3 text-amber-500" data-id="element-7238" /> : <User className="w-3 h-3 text-blue-500" data-id="element-7239" />}
                  {selectedSub.type === 'astrologer' ? 'Астролог' : 'Пользователь'}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-7240">
                <span className="text-slate-500" data-id="element-7241">Период</span>
                <span className="font-medium text-slate-900" data-id="element-7242">
                  {selectedSub.startDate} — {selectedSub.endDate}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-7243">
                <span className="text-slate-500" data-id="element-7244">Автопродление</span>
                <span className="font-medium text-slate-900 flex items-center gap-1" data-id="element-7245">
                  {selectedSub.autoRenew ? <Check className="w-3 h-3 text-emerald-500" data-id="element-7246" /> : <X className="w-3 h-3 text-rose-500" data-id="element-7247" />}
                  {selectedSub.autoRenew ? 'Включено' : 'Выключено'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div data-id="element-7248">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-7249">
              Быстрые действия
            </h4>
            <div className="grid grid-cols-2 gap-2" data-id="element-7250">
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'change_plan')} data-id="element-7251">
                <RefreshCw className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-7252" />{' '}
                Изменить план
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'manual_renew')} data-id="element-7253">
                <Calendar className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-7254" />{' '}
                Продлить вручную
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'toggle_auto_renew')} data-id="element-7255">
                <RefreshCcw className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-7256" />{' '}
                {selectedSub.autoRenew ? 'Откл. автоплатеж' : 'Вкл. автоплатеж'}
              </Button>
              {selectedSub.status === 'active' ? <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'pause')} data-id="element-7257">
                  <Pause className="w-3.5 h-3.5 mr-2 text-amber-500" data-id="element-7258" /> Пауза
                </Button> : selectedSub.status === 'paused' ? <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'resume')} data-id="element-7259">
                  <Play className="w-3.5 h-3.5 mr-2 text-emerald-500" data-id="element-7260" />{' '}
                  Возобновить
                </Button> : null}
              <Button variant="outline" size="sm" className="justify-start text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => handleAction(selectedSub.id, 'cancel')} data-id="element-7261">
                <XCircle className="w-3.5 h-3.5 mr-2" data-id="element-7262" /> Отменить
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs text-amber-600 hover:bg-amber-50 hover:text-amber-700" onClick={() => handleAction(selectedSub.id, 'refund')} data-id="element-7263">
                <DollarSign className="w-3.5 h-3.5 mr-2" data-id="element-7264" /> Возврат
              </Button>
            </div>
          </div>

          {/* Payment Errors (if any) */}
          {selectedSub.activeErrors.length > 0 && <div data-id="element-7265">
              <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-3 flex items-center gap-1" data-id="element-7266">
                <AlertTriangle className="w-4 h-4" data-id="element-7267" /> Ошибки оплаты
              </h4>
              <div className="space-y-2" data-id="element-7268">
                {selectedSub.activeErrors.map(err => <div key={err.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm" data-id="element-7269">
                    <div className="flex justify-between font-medium text-rose-900 mb-1" data-id="element-7270">
                      <span data-id="element-7271">
                        {err.amount} {selectedSub.currency}
                      </span>
                      <span data-id="element-7272">{err.date}</span>
                    </div>
                    <div className="text-rose-700 text-xs mb-2" data-id="element-7273">
                      Причина: {err.reason} (Попыток: {err.attempts})
                    </div>
                    <Button variant="primary" size="sm" className="w-full bg-rose-600 hover:bg-rose-700 text-xs py-1 h-auto" onClick={() => success('Запущен повторный биллинг')} data-id="element-7274">
                      Повторить списание
                    </Button>
                  </div>)}
              </div>
            </div>}

          {/* History */}
          <div data-id="element-7275">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-7276">
              История
            </h4>
            <div className="relative border-l-2 border-slate-100 ml-2 space-y-4 pb-4" data-id="element-7277">
              {selectedSub.history.map((evt, i) => <div key={evt.id} className="relative pl-4" data-id="element-7278">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${evt.type === 'payment_failed' ? 'bg-rose-500' : evt.type === 'cancelled' ? 'bg-slate-400' : 'bg-indigo-500'}`} data-id="element-7279" />
                  <div className="text-xs text-slate-500 mb-0.5" data-id="element-7280">
                    {evt.date} • {evt.actor}
                  </div>
                  <div className="text-sm font-medium text-slate-900" data-id="element-7281">
                    {evt.description}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderList = () => <div className="space-y-4" data-id="element-7282">
      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-7283">
        {renderKpi('total', 'Всего', metrics.total, <CreditCard className="w-4 h-4 text-indigo-600" data-id="element-7284" />, 'bg-indigo-50', 'text-indigo-900')}
        {renderKpi('active', 'Активные', metrics.active, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-7285" />, 'bg-emerald-50', 'text-emerald-900')}
        {renderKpi('trial', 'Пробные', metrics.trial, <Play className="w-4 h-4 text-blue-600" data-id="element-7286" />, 'bg-blue-50', 'text-blue-900')}
        {renderKpi('cancelled', 'Отмененные', metrics.cancelled, <XCircle className="w-4 h-4 text-slate-600" data-id="element-7287" />, 'bg-slate-100', 'text-slate-900')}
        {renderKpi('errors', 'Ошибки', metrics.errors, <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-7288" />, 'bg-rose-50', 'text-rose-900')}
        {renderKpi('mrr', 'MRR', `₽${Math.floor(metrics.mrr / 1000)}K`, <ArrowUpRight className="w-4 h-4 text-emerald-600" data-id="element-7289" />, 'bg-emerald-50', 'text-emerald-900')}
        {renderKpi('new', 'Новые (мес)', metrics.newThisMonth, <Star className="w-4 h-4 text-amber-600" data-id="element-7290" />, 'bg-amber-50', 'text-amber-900')}
        {renderKpi('renewals', 'Продления', metrics.renewals, <RefreshCw className="w-4 h-4 text-purple-600" data-id="element-7291" />, 'bg-purple-50', 'text-purple-900')}
      </div>

      {/* Toolbar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-7292">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-7293">
          <div className="relative flex-1 max-w-md w-full" data-id="element-7294">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-7295" />
            <input type="text" placeholder="Поиск по ID, имени, email..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7296" />
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-7297">
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-7299" />} onClick={() => setShowFilters(!showFilters)} className="flex-1 sm:flex-none" data-id="element-7298">
              Фильтры
            </Button>
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-7301" />} onClick={handleExportCSV} className="flex-1 sm:flex-none" data-id="element-7300">
              Экспорт
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3" data-id="element-7302">
            <div data-id="element-7303">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7304">
                Тип
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.type} onChange={e => setFilters({
            ...filters,
            type: e.target.value as any
          })} data-id="element-7305">
                <option value="all" data-id="element-7306">Все типы</option>
                <option value="astrologer" data-id="element-7307">Астрологи</option>
                <option value="user" data-id="element-7308">Пользователи</option>
              </select>
            </div>
            <div data-id="element-7309">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7310">
                План
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.plan} onChange={e => setFilters({
            ...filters,
            plan: e.target.value as any
          })} data-id="element-7311">
                <option value="all" data-id="element-7312">Все планы</option>
                {Object.entries(planLabels).map(([k, v]) => <option key={k} value={k} data-id="element-7313">
                    {v}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7314">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7315">
                Статус
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value as any
          })} data-id="element-7316">
                <option value="all" data-id="element-7317">Все статусы</option>
                {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k} data-id="element-7318">
                    {v.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7319">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7320">
                Автоплатеж
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.autoRenew} onChange={e => setFilters({
            ...filters,
            autoRenew: e.target.value as any
          })} data-id="element-7321">
                <option value="all" data-id="element-7322">Все</option>
                <option value="true" data-id="element-7323">Включен</option>
                <option value="false" data-id="element-7324">Выключен</option>
              </select>
            </div>
            <div className="flex items-end" data-id="element-7325">
              <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => setFilters({
            type: 'all',
            plan: 'all',
            status: 'all',
            autoRenew: 'all',
            source: 'all'
          })} data-id="element-7326">
                Сбросить
              </Button>
            </div>
          </div>}
      </Card>

      {/* Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-7327">
        <div className="overflow-x-auto" data-id="element-7328">
          <table className="w-full text-left whitespace-nowrap" data-id="element-7329">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-7330">
              <tr data-id="element-7331">
                <th className="px-4 py-3 font-medium" data-id="element-7332">ID / Пользователь</th>
                <th className="px-4 py-3 font-medium" data-id="element-7333">Тип / План</th>
                <th className="px-4 py-3 font-medium" data-id="element-7334">Цена</th>
                <th className="px-4 py-3 font-medium" data-id="element-7335">Период</th>
                <th className="px-4 py-3 font-medium" data-id="element-7336">Сл. списание</th>
                <th className="px-4 py-3 font-medium" data-id="element-7337">Авто</th>
                <th className="px-4 py-3 font-medium" data-id="element-7338">Статус</th>
                <th className="px-4 py-3 font-medium" data-id="element-7339">LTV / Оплаты</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-7340">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-7341">
              {paginatedSubs.length === 0 ? <tr data-id="element-7342">
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500" data-id="element-7343">
                    <div className="flex flex-col items-center justify-center" data-id="element-7344">
                      <Search className="w-8 h-8 text-slate-300 mb-2" data-id="element-7345" />
                      <p data-id="element-7346">Подписки не найдены</p>
                    </div>
                  </td>
                </tr> : paginatedSubs.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors cursor-pointer ${selectedSubId === s.id ? 'bg-indigo-50/50' : ''} ${s.status === 'payment_error' ? 'bg-rose-50/30' : ''}`} onClick={() => setSelectedSubId(s.id)} data-id="element-7347">
                    <td className="px-4 py-3" data-id="element-7348">
                      <div className="text-xs font-mono text-slate-500 mb-0.5" data-id="element-7349">
                        {s.id}
                      </div>
                      <div className="text-sm font-bold text-slate-900" data-id="element-7350">
                        {s.userName}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7351">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-7352">
                        {s.type === 'astrologer' ? <Star className="w-3 h-3 text-amber-500" data-id="element-7353" /> : <User className="w-3 h-3 text-blue-500" data-id="element-7354" />}
                        <span className="text-xs text-slate-600" data-id="element-7355">
                          {s.type === 'astrologer' ? 'Астролог' : 'Юзер'}
                        </span>
                      </div>
                      <Badge variant="default" size="sm" data-id="element-7356">
                        {planLabels[s.plan]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-7357">
                      <div className="text-sm font-medium text-slate-900" data-id="element-7358">
                        {s.price} {s.currency}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7359">
                        {s.billingPeriod === 'monthly' ? '/ мес' : '/ год'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-7360">
                      <div className="text-xs text-slate-900" data-id="element-7361">
                        {s.startDate}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7362">
                        до {s.endDate}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600" data-id="element-7363">
                      {s.nextChargeDate || '—'}
                    </td>
                    <td className="px-4 py-3" data-id="element-7364">
                      {s.autoRenew ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-7365" /> : <XCircle className="w-4 h-4 text-slate-300" data-id="element-7366" />}
                    </td>
                    <td className="px-4 py-3" data-id="element-7367">
                      <Badge variant={statusConfig[s.status].variant} size="sm" data-id="element-7368">
                        {statusConfig[s.status].label}
                      </Badge>
                      {s.activeErrors.length > 0 && <div className="text-[10px] text-rose-600 mt-1 flex items-center gap-1" data-id="element-7369">
                          <AlertTriangle className="w-3 h-3" data-id="element-7370" />{' '}
                          {s.activeErrors.length} ошиб.
                        </div>}
                    </td>
                    <td className="px-4 py-3" data-id="element-7371">
                      <div className="text-sm font-medium text-slate-900" data-id="element-7372">
                        {s.ltv.toLocaleString()} {s.currency}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7373">
                        {s.paymentsCount} успешных
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-7374">
                      <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" onClick={e => {
                  e.stopPropagation();
                  setSelectedSubId(s.id);
                }} data-id="element-7375">
                        <MoreVertical className="w-4 h-4" data-id="element-7376" />
                      </Button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-7377">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-7378" />
          </div>}
      </Card>
    </div>;
  const renderErrors = () => {
    const errorSubs = subscriptions.filter(s => s.activeErrors.length > 0);
    return <div className="space-y-4" data-id="element-7379">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-id="element-7380">
          <AlertOctagon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" data-id="element-7381" />
          <div data-id="element-7382">
            <h3 className="text-sm font-bold text-rose-900" data-id="element-7383">
              Ошибки списаний ({errorSubs.length})
            </h3>
            <p className="text-xs text-rose-700 mt-1" data-id="element-7384">
              Подписки с неудачными попытками списания средств. Система
              автоматически повторяет попытки согласно расписанию.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7385">
          {errorSubs.map(s => <Card key={s.id} padding="sm" className="border-rose-200 shadow-sm relative overflow-hidden" data-id="element-7386">
              <div className="absolute top-0 left-0 w-1 h-full bg-rose-500" data-id="element-7387" />
              <div className="flex justify-between items-start mb-3" data-id="element-7388">
                <div data-id="element-7389">
                  <div className="text-sm font-bold text-slate-900" data-id="element-7390">
                    {s.userName}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-7391">
                    {s.id} • {planLabels[s.plan]}
                  </div>
                </div>
                <Badge variant="error" size="sm" data-id="element-7392">
                  {s.price} {s.currency}
                </Badge>
              </div>

              <div className="space-y-2 mb-4" data-id="element-7393">
                {s.activeErrors.map(err => <div key={err.id} className="bg-slate-50 rounded p-2 text-xs" data-id="element-7394">
                    <div className="flex justify-between text-slate-700 mb-1" data-id="element-7395">
                      <span data-id="element-7396">Дата: {err.date}</span>
                      <span className="font-medium text-rose-600" data-id="element-7397">
                        Попыток: {err.attempts}
                      </span>
                    </div>
                    <div className="text-slate-600" data-id="element-7398">
                      Причина: <span className="font-medium" data-id="element-7399">{err.reason}</span>
                    </div>
                    {err.nextAttemptDate && <div className="text-slate-500 mt-1" data-id="element-7400">
                        След. попытка: {err.nextAttemptDate}
                      </div>}
                  </div>)}
              </div>

              <div className="flex gap-2" data-id="element-7401">
                <Button variant="primary" size="sm" className="flex-1 bg-rose-600 hover:bg-rose-700 text-xs py-1.5 h-auto" onClick={() => success('Запущен повторный биллинг')} data-id="element-7402">
                  Повторить списание
                </Button>
                <Button variant="outline" size="sm" className="flex-1 text-xs py-1.5 h-auto" onClick={() => {
              setSelectedSubId(s.id);
              setActiveView('list');
            }} data-id="element-7403">
                  Детали
                </Button>
              </div>
            </Card>)}
          {errorSubs.length === 0 && <div className="col-span-full py-12 text-center text-slate-500" data-id="element-7404">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" data-id="element-7405" />
              <p className="text-lg font-medium text-slate-900" data-id="element-7406">
                Нет ошибок оплаты
              </p>
              <p className="text-sm mt-1" data-id="element-7407">
                Все автоматические списания проходят успешно.
              </p>
            </div>}
        </div>
      </div>;
  };
  const renderPlans = () => <div className="space-y-6" data-id="element-7408">
      <div className="flex justify-between items-center" data-id="element-7409">
        <h3 className="text-lg font-bold text-slate-900" data-id="element-7410">Тарифные планы</h3>
        <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-7412" />} onClick={() => success('Открыта форма создания тарифа')} data-id="element-7411">
          Создать тариф
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-7413">
        {/* Astrologer Plans */}
        <div data-id="element-7414">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7415">
            <Star className="w-4 h-4 text-amber-500" data-id="element-7416" /> Для астрологов
          </h4>
          <div className="space-y-4" data-id="element-7417">
            {mockTariffPlans.filter(p => p.type === 'astrologer').map(plan => <Card key={plan.id} padding="sm" className="border-slate-200" data-id="element-7418">
                  <div className="flex justify-between items-start mb-2" data-id="element-7419">
                    <div data-id="element-7420">
                      <div className="flex items-center gap-2" data-id="element-7421">
                        <h5 className="font-bold text-slate-900" data-id="element-7422">
                          {plan.name}
                        </h5>
                        {!plan.isActive && <Badge variant="neutral" size="sm" data-id="element-7423">
                            Архив
                          </Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-7424">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7425">
                      <div className="font-bold text-indigo-600" data-id="element-7426">
                        {plan.priceMonthly} {plan.currency}/мес
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7427">
                        {plan.priceYearly} {plan.currency}/год
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1" data-id="element-7428">
                    {plan.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-600" data-id="element-7429">
                        <Check className="w-3 h-3 text-emerald-500" data-id="element-7430" /> {f}
                      </div>)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-7431">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-7432">
                      <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-7433">
                        Триал: {plan.trialDays} дней
                      </span>
                      {plan.limits.packageDiscount && plan.limits.packageDiscount > 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200" data-id="element-7434">
                            Пакеты: -{plan.limits.packageDiscount}%
                          </span>}
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => success('Редактирование тарифа')} data-id="element-7435">
                      Настроить
                    </Button>
                  </div>
                </Card>)}
          </div>
        </div>

        {/* User Plans */}
        <div data-id="element-7436">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7437">
            <User className="w-4 h-4 text-blue-500" data-id="element-7438" /> Для пользователей
          </h4>
          <div className="space-y-4" data-id="element-7439">
            {mockTariffPlans.filter(p => p.type === 'user').map(plan => <Card key={plan.id} padding="sm" className="border-slate-200" data-id="element-7440">
                  <div className="flex justify-between items-start mb-2" data-id="element-7441">
                    <div data-id="element-7442">
                      <div className="flex items-center gap-2" data-id="element-7443">
                        <h5 className="font-bold text-slate-900" data-id="element-7444">
                          {plan.name}
                        </h5>
                        {!plan.isActive && <Badge variant="neutral" size="sm" data-id="element-7445">
                            Архив
                          </Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-7446">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7447">
                      <div className="font-bold text-indigo-600" data-id="element-7448">
                        {plan.priceMonthly} {plan.currency}/мес
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7449">
                        {plan.priceYearly} {plan.currency}/год
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1" data-id="element-7450">
                    {plan.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-600" data-id="element-7451">
                        <Check className="w-3 h-3 text-emerald-500" data-id="element-7452" /> {f}
                      </div>)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-7453">
                    <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-7454">
                      Триал: {plan.trialDays} дней
                    </span>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => success('Редактирование тарифа')} data-id="element-7455">
                      Настроить
                    </Button>
                  </div>
                </Card>)}
          </div>
        </div>
      </div>
    </div>;
  const renderAnalytics = () => <div className="space-y-6" data-id="element-7456">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7457">
        <Card padding="sm" className="border-slate-200" data-id="element-7458">
          <div className="text-sm text-slate-500 mb-1" data-id="element-7459">
            Удержание (Retention)
          </div>
          <div className="text-3xl font-bold text-emerald-600" data-id="element-7460">
            {mockAnalytics.retentionRate}%
          </div>
          <div className="text-xs text-emerald-600 flex items-center gap-1 mt-2" data-id="element-7461">
            <ArrowUpRight className="w-3 h-3" data-id="element-7462" /> +2.4% к прошлому месяцу
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200" data-id="element-7463">
          <div className="text-sm text-slate-500 mb-1" data-id="element-7464">Отток (Churn Rate)</div>
          <div className="text-3xl font-bold text-rose-600" data-id="element-7465">
            {mockAnalytics.churnRate}%
          </div>
          <div className="text-xs text-emerald-600 flex items-center gap-1 mt-2" data-id="element-7466">
            <ArrowRight className="w-3 h-3" data-id="element-7467" /> В пределах нормы
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200" data-id="element-7468">
          <div className="text-sm text-slate-500 mb-1" data-id="element-7469">ARPU</div>
          <div className="text-3xl font-bold text-indigo-600" data-id="element-7470">
            {mockAnalytics.arpu} ₽
          </div>
          <div className="text-xs text-indigo-600 flex items-center gap-1 mt-2" data-id="element-7471">
            <ArrowUpRight className="w-3 h-3" data-id="element-7472" /> +150 ₽ к прошлому месяцу
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-7473">
        <Card padding="sm" className="border-slate-200" data-id="element-7474">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-7475">
            Доход по планам (MRR)
          </h3>
          <div className="space-y-4" data-id="element-7476">
            {Object.entries(mockAnalytics.revenueByPlan).map(([plan, rev]) => {
            const percentage = rev / mockAnalytics.mrr * 100;
            return <div key={plan} data-id="element-7477">
                  <div className="flex justify-between text-xs mb-1" data-id="element-7478">
                    <span className="font-medium text-slate-700" data-id="element-7479">
                      {planLabels[plan as SubscriptionPlan]}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-7480">
                      {rev.toLocaleString()} ₽
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-7481">
                    <div className="bg-indigo-500 h-2 rounded-full" style={{
                  width: `${percentage}%`
                }} data-id="element-7482" />
                  </div>
                </div>;
          })}
          </div>
        </Card>

        <Card padding="sm" className="border-slate-200" data-id="element-7483">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-7484">
            Воронка конверсии
          </h3>
          <div className="space-y-4" data-id="element-7485">
            <div className="relative pt-2" data-id="element-7486">
              <div className="flex justify-between text-xs mb-1" data-id="element-7487">
                <span className="text-slate-500" data-id="element-7488">Регистрации</span>
                <span className="font-bold" data-id="element-7489">100% (5,240)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-7490">
                <div className="bg-slate-300 h-full w-full" data-id="element-7491" />
              </div>
            </div>
            <div className="relative" data-id="element-7492">
              <div className="flex justify-between text-xs mb-1" data-id="element-7493">
                <span className="text-slate-500" data-id="element-7494">Начали Trial</span>
                <span className="font-bold" data-id="element-7495">45% (2,358)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-7496">
                <div className="bg-blue-400 h-full w-[45%]" data-id="element-7497" />
              </div>
            </div>
            <div className="relative" data-id="element-7498">
              <div className="flex justify-between text-xs mb-1" data-id="element-7499">
                <span className="text-slate-500" data-id="element-7500">Первая оплата</span>
                <span className="font-bold text-emerald-600" data-id="element-7501">18% (943)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-7502">
                <div className="bg-emerald-500 h-full w-[18%]" data-id="element-7503" />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>;
  return <div className="space-y-6 relative" data-id="element-7504">
      {/* Header & Tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 pb-4" data-id="element-7505">
        <div data-id="element-7506">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-7507">
            Управление подписками
          </h2>
          <p className="text-sm text-slate-500" data-id="element-7508">
            Единая система подписок для астрологов и пользователей
          </p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-7509">
          <button onClick={() => setActiveView('list')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7510">
            Список
          </button>
          <button onClick={() => setActiveView('errors')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeView === 'errors' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7511">
            Ошибки{' '}
            {subscriptions.filter(s => s.activeErrors.length > 0).length > 0 && <span className="w-2 h-2 rounded-full bg-rose-500" data-id="element-7512" />}
          </button>
          <button onClick={() => setActiveView('plans')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'plans' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7513">
            Тарифы
          </button>
          <button onClick={() => setActiveView('analytics')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'analytics' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7514">
            Аналитика
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="relative" data-id="element-7515">
        {activeView === 'list' && renderList()}
        {activeView === 'errors' && renderErrors()}
        {activeView === 'plans' && renderPlans()}
        {activeView === 'analytics' && renderAnalytics()}

        {/* Right Panel Overlay */}
        {selectedSubId && <>
            <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 transition-opacity" onClick={() => setSelectedSubId(null)} data-id="element-7516" />
            {renderRightPanel()}
          </>}
      </div>
    </div>;
}